import { useEffect, useState } from "react";
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  Switch,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Save24Regular } from "@fluentui/react-icons";
import { STATI, type Stato } from "@app/schema";
import { GraphError } from "@app/sp-data";
import { useData } from "../../data/DataProvider";
import { useRichiesta } from "../../data/useRichiesta";
import { salvaGestione, type Richiesta } from "../../data/richieste";
import { formatDateTime, formatNumberInput, parseNumber } from "../../lib/format";
import { goBack } from "../../lib/nav";
import { Shell } from "../../components/Shell";
import { RichiestaInfo } from "../../components/RichiestaInfo";
import { InfoRow, Section } from "../../components/Section";
import { ChipGroup } from "../../components/ChipGroup";
import { FornitoreField } from "../../components/FornitoreField";
import { ErrorState, describeError } from "../../components/ErrorState";

const useStyles = makeStyles({
  row: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  two: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: tokens.spacingHorizontalM },
  input: { width: "100%", minWidth: 0 },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

/** Passaggi più probabili da ciascuno stato (mostrati per primi). */
const PROSSIMI: Record<Stato, Stato[]> = {
  Sottomessa: ["Presa visione", "In lavorazione", "Respinta"],
  "Presa visione": ["In lavorazione", "Respinta"],
  "In lavorazione": ["Evasa", "Bloccata"],
  Bloccata: ["In lavorazione", "Respinta"],
  Respinta: [],
  Evasa: [],
};

export function GestisciRichiesta({ id }: { id: number }) {
  const { item, error, reload } = useRichiesta(id);
  const [saved, setSaved] = useState(false);
  if (item)
    return (
      <Editor
        key={item.etag}
        r={item}
        saved={saved}
        onSaved={() => {
          setSaved(true);
          window.scrollTo({ top: 0, behavior: "smooth" });
          void reload();
        }}
        onReload={() => {
          setSaved(false);
          void reload();
        }}
      />
    );
  return (
    <Shell title={`Richiesta #${id}`} onBack={goBack}>
      {error ? (
        <ErrorState error={error} />
      ) : item === null ? (
        <ErrorState error={new Error("Richiesta non trovata.")} />
      ) : (
        <Spinner style={{ marginTop: 48 }} />
      )}
    </Shell>
  );
}

function Editor({ r, saved, onSaved, onReload }: { r: Richiesta; saved: boolean; onSaved: () => void; onReload: () => void }) {
  const s = useStyles();
  const data = useData();
  const f = r.fields;
  const [stato, setStato] = useState<Stato>((f.Stato as Stato) ?? "Sottomessa");
  const [quantita, setQuantita] = useState(formatNumberInput(f.Quantita));
  const [valore, setValore] = useState(formatNumberInput(f.Valore));
  const [extra, setExtra] = useState(!!f.ExtraBudget);
  const [note, setNote] = useState(f.NoteGestore ?? "");
  const [fornitore, setFornitore] = useState<number | undefined>(f.Fornitore);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ intent: "success" | "error" | "warning"; text: string; conflict?: boolean } | undefined>(
    saved ? { intent: "success", text: "Modifiche salvate." } : undefined,
  );
  const [gestitoDa, setGestitoDa] = useState<string>();

  useEffect(() => {
    if (f.GestitoDa) void data.users.byId(f.GestitoDa).then((u) => setGestitoDa(u?.displayName));
  }, [data, f.GestitoDa]);

  const corrente = (f.Stato as Stato) ?? "Sottomessa";
  const ordine = [corrente, ...PROSSIMI[corrente], ...STATI.filter((x) => x !== corrente && !PROSSIMI[corrente].includes(x))];

  async function save() {
    const q = parseNumber(quantita);
    const v = parseNumber(valore);
    if (quantita.trim() && q === undefined) return setMsg({ intent: "error", text: "Quantità non valida." });
    if (valore.trim() && v === undefined) return setMsg({ intent: "error", text: "Valore non valido." });
    if (stato === "Respinta" && !note.trim())
      return setMsg({ intent: "error", text: "Per respingere una richiesta scrivi la motivazione nelle note." });

    setSaving(true);
    setMsg(undefined);
    try {
      await salvaGestione(data, r, { stato, quantita: q, valore: v, extraBudget: extra, noteGestore: note, fornitore });
      onSaved();
    } catch (e) {
      const conflict = e instanceof GraphError && e.isConflict;
      setMsg({ intent: conflict ? "warning" : "error", text: describeError(e).detail, conflict });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Shell
      title={`Richiesta #${r.id}`}
      onBack={goBack}
      footer={
        <Button appearance="primary" size="large" icon={<Save24Regular />} style={{ flexGrow: 1 }} onClick={save} disabled={saving}>
          {saving ? "Salvataggio…" : stato !== corrente ? `Salva: ${stato}` : "Salva"}
        </Button>
      }
    >
      {msg && (
        <MessageBar intent={msg.intent}>
          <MessageBarBody>{msg.text}</MessageBarBody>
          {msg.conflict && (
            <MessageBarActions>
              <Button size="small" onClick={onReload}>
                Ricarica
              </Button>
            </MessageBarActions>
          )}
        </MessageBar>
      )}

      <RichiestaInfo r={r} showStato />

      <Section title="Gestione">
        <Field label="Stato" hint={stato !== corrente ? `Da "${corrente}" a "${stato}"` : undefined}>
          <ChipGroup ariaLabel="Stato" options={ordine} value={stato} onChange={setStato} />
        </Field>
        <FornitoreField r={r} value={fornitore} onChange={setFornitore} />
        <div className={s.two}>
          <Field label="Quantità">
            <Input className={s.input} input={{ style: { minWidth: 0 } }} size="large" inputMode="decimal" value={quantita} onChange={(_, d) => setQuantita(d.value)} />
          </Field>
          <Field label="Valore (€)">
            <Input className={s.input} input={{ style: { minWidth: 0 } }} size="large" inputMode="decimal" value={valore} placeholder="0,00" onChange={(_, d) => setValore(d.value)} />
          </Field>
        </div>
        <div className={s.row}>
          <span>Extra budget</span>
          <Switch checked={extra} onChange={(_, d) => setExtra(d.checked)} aria-label="Extra budget" />
        </div>
        <Field label="Note gestore" hint="Visibili al richiedente.">
          <Textarea size="large" rows={3} resize="vertical" value={note} onChange={(_, d) => setNote(d.value)} />
        </Field>
      </Section>

      {(gestitoDa || f.DataChiusura) && (
        <Section title="Storico">
          {gestitoDa && <InfoRow label="Gestita da">{gestitoDa}</InfoRow>}
          {f.DataChiusura && <InfoRow label="Chiusa il">{formatDateTime(f.DataChiusura)}</InfoRow>}
          <InfoRow label="Ultima modifica">
            {formatDateTime(r.modifiedAt)}
            {r.modifiedBy?.displayName ? ` · ${r.modifiedBy.displayName}` : ""}
          </InfoRow>
        </Section>
      )}
    </Shell>
  );
}
